import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Lock } from 'lucide-react';



const AdminLogin = () => {
  const { language, t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Invalid credentials');
      const data = await response.json();
      
      // Store token in localStorage
      localStorage.setItem('admin_token', data.access_token);
      
      toast({
        title: t({ en: 'Login Successful!', bn: 'লগইন সফল!' }),
        description: t({
          en: 'Welcome to the admin panel.',
          bn: 'অ্যাডমিন প্যানেলে স্বাগতম।',
        }),
      });

      // Redirect to admin dashboard
      navigate('/admin/dashboard');
    } catch (error) {
      toast({
        title: t({ en: 'Login Failed', bn: 'লগইন ব্যর্থ' }),
        description: t({
          en: 'Invalid username or password.',
          bn: 'ভুল ইউজারনেম বা পাসওয়ার্ড।',
        }),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-party-red-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-party-red" />
          </div>
          <CardTitle className="text-2xl">
            {t({ en: 'Admin Login', bn: 'অ্যাডমিন লগইন' })}
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {t({
              en: 'Sign in to access the admin panel',
              bn: 'অ্যাডমিন প্যানেল অ্যাক্সেস করতে সাইন ইন করুন',
            })}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username">
                {t({ en: 'Username', bn: 'ইউজারনেম' })}
              </Label>
              <Input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                required
                className="mt-1"
                placeholder={t({ en: 'Enter username', bn: 'ইউজারনেম লিখুন' })}
              />
            </div>

            <div>
              <Label htmlFor="password">
                {t({ en: 'Password', bn: 'পাসওয়ার্ড' })}
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="mt-1"
                placeholder={t({ en: 'Enter password', bn: 'পাসওয়ার্ড লিখুন' })}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-party-red hover:bg-party-red-600"
              disabled={loading}
            >
              {loading
                ? t({ en: 'Signing in...', bn: 'সাইন ইন হচ্ছে...' })
                : t({ en: 'Sign In', bn: 'সাইন ইন' })}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 font-semibold mb-2">
              {t({ en: 'Admin Credentials:', bn: 'অ্যাডমিন শংসাপত্র:' })}
            </p>
            <p className="text-sm text-blue-700">
              <strong>{t({ en: 'Username:', bn: 'ইউজারনেম:' })}</strong> admin
            </p>
            <p className="text-sm text-blue-700">
              <strong>{t({ en: 'Password:', bn: 'পাসওয়ার্ড:' })}</strong> alitravels2026
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
